import { Injectable } from '@angular/core'
import { WebService } from '../api/web.service'
import { ServiceUrlConstants } from 'src/app/constants/service-url-constants'
import { Observable, map } from 'rxjs'
import { ActivityAPIModel, ModuleDashboardAPIModel, PaginatedAPI, updatePending } from 'src/app/models/api.model'
import { Store } from '@ngrx/store'
import { AppState } from 'src/app/store/state/app.state'
import {
  DELETE_ACTIVITY_DETAILS,
  FETCH_ACTIVITY_DETAILS,
  UPDATE_ACTIVITY_ACTION,
  fetch_activity_dashboard_data,
  save_activity_action,
  update_activity_pending_item
} from 'src/app/store/action/activity.action'
import { ActivityDashboardModel } from 'src/app/models/model'
import { getActivityDashboardInfo, getActivityDetails } from 'src/app/store/selector/activity.selector'

@Injectable({
  providedIn: 'root'
})
export class ActivityService {
  constructor(private webservice: WebService, private appState: Store<AppState>) {}

  dispatchFetchActivityDashboardDataAction(days: number, sortBy: string, sortOrder: string) {
    this.appState.dispatch(fetch_activity_dashboard_data({ days, sortBy, sortOrder }))
  }

  callDashboardApi = (days: number, sortBy: string, sortOrder: string) => {
    const endPoint = `${ServiceUrlConstants.API_ACTVITY_DASHBOARD}?days=${days}&sortBy=${sortBy}&sortOrder=${sortOrder}`
    return this.webservice.get(endPoint).pipe(map((data: ModuleDashboardAPIModel) => data))
  }

  getActivityDashboardInfo(): Observable<ActivityDashboardModel> {
    return this.appState.select(getActivityDashboardInfo)
  }

  dispatchSaveActivityAction(activity: ActivityAPIModel, files: File[], routeTo: string) {
    this.appState.dispatch(save_activity_action({ activity, files, routeTo }))
  }

  callSaveApi = (activity: ActivityAPIModel, files: File[]) => {
    const endPoint = ServiceUrlConstants.API_SAVE_ACTIVITY
    const formData: FormData = new FormData()
    const activityBlob = new Blob([JSON.stringify(activity)], { type: 'application/json' })
    formData.append('activity', activityBlob)

    if (files) {
      for (let i = 0; i < files.length; i++) {
        formData.append('documentList', files[i])
      }
    }

    return this.webservice.post(endPoint, formData, null, [], false).pipe(map((data: ActivityAPIModel) => data))
  }

  dispatchFetchActivityDetailsAction(id: string) {
    return this.appState.dispatch(FETCH_ACTIVITY_DETAILS({ id }))
  }

  callActivityDetailsApi = (id: string) => {
    const endPoint = `${ServiceUrlConstants.API_FETCH_ACTIVITY_DETAILS}${id}`
    return this.webservice.get(endPoint).pipe(map((data: ActivityAPIModel) => data))
  }

  getActivityDetailsFromStore = (): Observable<ActivityAPIModel> => {
    return this.appState.select(getActivityDetails())
  }

  dispatchUpdateActivityAction(activity: Partial<ActivityAPIModel>, id: string, files: File[], routeTo: string) {
    this.appState.dispatch(UPDATE_ACTIVITY_ACTION({ activity, id, files, routeTo }))
  }

  callUpdateApi = (activity: Partial<ActivityAPIModel>, id: string, files?: File[]) => {
    const endPoint = ServiceUrlConstants.API_UPDATE_ACTIVITY + id
    const formData: FormData = new FormData()
    const activityBlob = new Blob([JSON.stringify(activity)], { type: 'application/json' })
    formData.append('activity', activityBlob)

    if (files) {
      for (let i = 0; i < files.length; i++) {
        formData.append('documentList', files[i])
      }
    }

    return this.webservice.put(endPoint, formData, null, [], false).pipe(map((data: ActivityAPIModel) => data))
  }

  dispatchDeleteActivityDetailsAction() {
    return this.appState.dispatch(DELETE_ACTIVITY_DETAILS())
  }

  invokeActivitySummaryAPI = (queryParams: { key: string; value: string }[], searchRequestBody) => {
    return this.webservice
      .post(ServiceUrlConstants.API_ACTIVITY_SUMMARY_LIST, searchRequestBody, null, queryParams)
      .pipe(map((data: PaginatedAPI<ActivityAPIModel>) => data))
  }

  dispatchBulkUpdate(data: updatePending) {
    this.appState.dispatch(update_activity_pending_item({ data }))
  }

  bulkUpdatePendingActivityAPI(searchRequestBody) {
    return this.webservice
      .put(ServiceUrlConstants.API_UPDATE_PENDING, searchRequestBody.data, null)
      .pipe(map((data: PaginatedAPI<ActivityAPIModel>) => data))
  }

  callActivityType() {
    return this.webservice.get(ServiceUrlConstants.API_ACTIVITY_TYPE).pipe(map((data: ActivityAPIModel) => data))
  }

  invokeSaveBulkActivityAPI(rowsToSave: ActivityAPIModel[], documentToSave: File): Observable<any> {
    const endPoint = ServiceUrlConstants.API_SAVE_BULK_ACTIVITY
    const formData: FormData = new FormData()
    const activityBlob = new Blob([JSON.stringify(rowsToSave)], { type: 'application/json' })
    formData.append('activities', activityBlob)

    if (documentToSave) {
      formData.append('documents', documentToSave)
    }

    return this.webservice.post(endPoint, formData, null, [], false)
  }
}
